
import React, { useEffect, useState, useRef } from 'react';
import './LandingPage.css';
import { productsAPI } from '../../services/api';
import { Product } from '../../context/AppContext';
import { getStoredUtm } from '../../utils/utm';

interface LandingPageProps {
  onGetStarted: () => void;
  /** Header "Sign In" CTA — defaults to onGetStarted if not provided so
   *  the prop is backwards-compatible. App.tsx supplies a different
   *  callback that routes to the login form (mode='signin') instead of
   *  the create-account form (mode='signup'). */
  onSignIn?: () => void;
  /** "Shop the Closet" CTA — routes cold visitors into the public
   *  guest-shop view instead of the subscription funnel. If omitted,
   *  the secondary shop CTA is hidden (keeps the prop backwards-compat
   *  with any test harness still passing only the two callbacks). */
  onShopClick?: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted, onSignIn, onShopClick }) => {
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState<{[key: string]: boolean}>({});
  const observerRefs = useRef<{[key: string]: HTMLDivElement | null}>({});
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  // Live "Just Dropped" feed. GET /api/products is public, so we can
  // pull current featured listings without auth. Fails silently — if
  // the API is down or returns 0 items the section just doesn't render.
  const [justDropped, setJustDropped] = useState<Product[]>([]);
  // Reddit-aware hero. If the visitor arrived with a stored UTM that
  // matches reddit (utm_source) OR `ref=reddit`, we promote the shop
  // CTA to primary and demote the subscription CTA to secondary —
  // matching what Reddit traffic actually wants to do (browse first,
  // subscribe later). Falls back to the default subscription-first
  // hero for everyone else.
  const utm = typeof window !== 'undefined' ? getStoredUtm() : null;
  const isRedditTraffic = Boolean(
    onShopClick && utm && (
      (utm.source && /reddit/i.test(utm.source)) ||
      (utm.ref && /reddit/i.test(utm.ref))
    )
  );

  useEffect(() => {
    if (!onShopClick) return; // section only useful when the shop CTA is wired
    let cancelled = false;
    productsAPI.getAll({ featured: true })
      .then(({ products }) => {
        if (cancelled) return;
        const top = (products || []).slice(0, 6) as unknown as Product[];
        setJustDropped(top);
      })
      .catch(() => { /* silent — section just hides */ });
    return () => { cancelled = true; };
  }, [onShopClick]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    Object.values(observerRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const setRef = (id: string) => (el: HTMLDivElement | null) => {
    observerRefs.current[id] = el;
  };

  const benefits = [
    { icon: '📸', title: 'Exclusive Photos', desc: 'Behind-the-scenes content you won\'t find anywhere else', delay: 0 },
    { icon: '🎬', title: 'Personal Updates', desc: 'Stay connected with daily life moments', delay: 100 },
    { icon: '💬', title: 'Direct Messages', desc: 'Get direct access to connect and chat', delay: 200 },
    { icon: '🎁', title: 'Special Perks', desc: 'Subscriber-only giveaways and surprises', delay: 300 }
  ];

  const stats = [
    { value: '25K+', label: 'Subscribers' },
    { value: '500+', label: 'Exclusive Posts' },
    { value: '24/7', label: 'Access' },
    { value: '4.9', label: 'Rating' }
  ];

  const contentTypes = [
    { icon: '📷', title: 'Photo Sets', count: '200+', desc: 'High-quality exclusive photoshoots' },
    { icon: '🎥', title: 'Videos', count: '150+', desc: 'Behind-the-scenes & personal vlogs' },
    { icon: '🎤', title: 'Live Streams', count: 'Weekly', desc: 'Interactive live sessions' },
    { icon: '📱', title: 'Stories', count: 'Daily', desc: 'Day-in-the-life updates' },
    { icon: '🎵', title: 'Audio', count: '50+', desc: 'Personal messages & content' },
    { icon: '✨', title: 'Exclusive', count: 'VIP', desc: 'Member-only special content' }
  ];

  const pricingPlans = [
    { id: 'monthly', name: '1 Month', price: 25, period: '/month', features: ['All exclusive content', 'Direct messaging', 'New posts daily', 'Full archive access'], popular: false },
    { id: 'quarterly', name: '3 Months', price: 40, period: '/3 months', features: ['Everything in Monthly', 'Save $35', 'Priority support', 'Early access'], popular: true, savings: 'Save $35' },
    { id: 'yearly', name: '1 Year', price: 100, period: '/year', features: ['Everything in 3 Months', 'Save $200', 'VIP chat access', 'Exclusive perks'], popular: false, savings: 'Save $200' },
    { id: 'lifetime', name: 'Lifetime', price: 250, period: 'one-time', features: ['Everything forever', 'All future content', 'Lifetime VIP status', 'Ultimate value'], popular: false, best: true }
  ];

  const faqs = [
    { q: 'What do I get with a subscription?', a: 'You get access to all exclusive photos, videos, behind-the-scenes content, direct messaging, live streams, and special subscriber-only perks. New content is posted daily!' },
    { q: 'Can I cancel anytime?', a: 'Yes! You can cancel your subscription at any time. Your access will continue until the end of your billing period.' },
    { q: 'How often is new content posted?', a: 'New content is posted daily! You\'ll get photos, videos, stories, and live streams throughout the week to keep you entertained.' },
    { q: 'Is my payment secure?', a: 'Absolutely! We use industry-standard encryption and secure payment processing. Your financial information is never stored on our servers.' },
    { q: 'Can I message directly?', a: 'Yes! All subscribers get access to direct messaging. Premium and VIP members get priority responses.' },
    { q: 'What\'s included in the Lifetime plan?', a: 'The Lifetime plan gives you permanent access to all current and future content, VIP status, exclusive perks, and never having to pay again!' }
  ];

  const howItWorks = [
    { step: '1', title: 'Choose Your Plan', desc: 'Select the subscription that fits your needs', icon: '📋' },
    { step: '2', title: 'Create Account', desc: 'Quick and easy signup process', icon: '✨' },
    { step: '3', title: 'Instant Access', desc: 'Unlock all exclusive content immediately', icon: '🔓' },
    { step: '4', title: 'Enjoy & Connect', desc: 'Watch, like, comment, and message', icon: '❤️' }
  ];

  // TODO: Add real social media URLs when available
  const socialLinks = [
    { platform: 'Instagram', icon: '📸', followers: '@built_by_ward', link: 'https://instagram.com/built_by_ward' },
    { platform: 'TikTok', icon: '🎵', followers: '@builtbyward', link: 'https://www.tiktok.com/@builtbyward' },
    { platform: 'Twitter', icon: '🐦', followers: 'Coming Soon', link: '#' },
    { platform: 'YouTube', icon: '▶️', followers: 'Coming Soon', link: '#' }
  ];

  const galleryImages = [
    '/images/portfolio-1/1.png',
    '/images/portfolio-1/2.png',
    '/images/portfolio-1/3.png',
    '/images/portfolio-1/4.png',
    '/images/portfolio-1/5.png',
    '/images/portfolio-1/6.png'
  ];

  return (
    <div className="landing-page">
      {/* Animated Background */}
      <div className="animated-bg">
        <div className="gradient-orb orb-1" style={{ transform: `translate(${scrollY * 0.1}px, ${scrollY * 0.05}px)` }} />
        <div className="gradient-orb orb-2" style={{ transform: `translate(${-scrollY * 0.08}px, ${scrollY * 0.03}px)` }} />
        <div className="gradient-orb orb-3" style={{ transform: `translate(${scrollY * 0.05}px, ${-scrollY * 0.02}px)` }} />
        <div className="grid-overlay" />
      </div>

      {/* Header */}
      <header className={`landing-header ${scrollY > 50 ? 'scrolled' : ''}`}>
        <div className="header-content">
          <div className="landing-logo">
            <img src="https://jalenward.com/images/blackwhitetextjalen.png" alt="OnlyJalen" className="logo-image" />
          </div>
          <nav className="header-nav">
            <a href="#content">Content</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
          </nav>
          <button className="header-cta" onClick={onSignIn || onGetStarted}>
            <span>Sign In</span>
            <div className="btn-glow" />
          </button>
        </div>
      </header>

      <main className="landing-main">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-content">
            <div className="hero-logo-container">
              <img src="https://jalenward.com/images/logosmall.png" alt="Jalen Ward" className="hero-main-logo" />
            </div>

            {isRedditTraffic && (
              <div className="reddit-banner animate-fade-in delay-2" role="note">
                <span className="reddit-banner-icon">👋</span>
                <span>From Reddit? Skip the wall — shop the closet first.</span>
              </div>
            )}

            <h1 className="hero-title">
              {isRedditTraffic ? (
                <>
                  <span className="title-line animate-slide-up delay-1">Shop my</span>
                  <span className="title-line animate-slide-up delay-2">
                    <span className="gradient-text">closet</span>
                  </span>
                </>
              ) : (
                <>
                  <span className="title-line animate-slide-up delay-1">Step into</span>
                  <span className="title-line animate-slide-up delay-2">
                    <span className="gradient-text">my world</span>
                  </span>
                </>
              )}
            </h1>

            <p className="hero-description animate-fade-in delay-3">
              {isRedditTraffic ? (
                <>
                  Real 1-of-1 pieces from my life. Every purchase comes with exclusive photos.
                  <span className="hero-tagline"> Submit your offer.</span>
                </>
              ) : (
                <>
                  Exclusive content. Behind-the-scenes access. Direct connection.
                  <span className="hero-tagline"> Join the inner circle.</span>
                </>
              )}
            </p>

            <div className="hero-cta animate-scale-in delay-4">
              {isRedditTraffic && onShopClick ? (
                <>
                  <button className="primary-btn pulse-btn" onClick={onShopClick}>
                    <span className="btn-content">
                      <span>🛍 Shop the Closet</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </span>
                    <div className="btn-shine" />
                  </button>
                  <button type="button" className="secondary-btn shop-btn" onClick={onGetStarted}>
                    <span className="btn-content">
                      <span>Subscribe instead</span>
                    </span>
                  </button>
                  <span className="cta-subtext">1-of-1 drops • Bidding • PayPal secured</span>
                </>
              ) : (
                <>
                  <button className="primary-btn pulse-btn" onClick={onGetStarted}>
                    <span className="btn-content">
                      <span>Get Started</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </span>
                    <div className="btn-shine" />
                  </button>
                  {onShopClick && (
                    <button type="button" className="secondary-btn shop-btn" onClick={onShopClick}>
                      <span className="btn-content">
                        <span>🛍 Shop the Closet</span>
                      </span>
                    </button>
                  )}
                  <span className="cta-subtext">Join 25,000+ subscribers</span>
                </>
              )}
            </div>

            {/* Quick Stats Under CTA */}
            <div className="hero-quick-stats animate-fade-in delay-5">
              <div className="quick-stat">
                <span className="quick-stat-icon">🔥</span>
                <span className="quick-stat-text">New content daily</span>
              </div>
              <div className="quick-stat">
                <span className="quick-stat-icon">💬</span>
                <span className="quick-stat-text">Direct messaging</span>
              </div>
              <div className="quick-stat">
                <span className="quick-stat-icon">🎁</span>
                <span className="quick-stat-text">Exclusive perks</span>
              </div>
            </div>
          </div>

          {/* Hero Visual - Mobile Optimized */}
          <div className="hero-visual-mobile">
            <div className="phone-mockup">
              <div className="phone-notch" />
              <div className="phone-screen">
                <div className="screen-header">
                  <div className="profile-row">
                    <div className="avatar-ring">
                      <img src="https://jalenward.com/images/trainer-hero.jpg" alt="Jalen" className="profile-avatar" />
                    </div>
                    <div className="profile-info">
                      <span className="profile-name">Jalen Ward</span>
                      <span className="profile-handle">@onlyjalen</span>
                    </div>
                    <div className="verified-badge">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="content-preview">
                  <img src="https://jalenward.com/images/trainer-hero.jpg" alt="Jalen" className="preview-img" />
                </div>

                <div className="screen-actions">
                  <div className="action-btn">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                    <span>2.4K</span>
                  </div>
                  <div className="action-btn">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                    <span>184</span>
                  </div>
                  <div className="action-btn">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                      <polyline points="16 6 12 2 8 6"/>
                      <line x1="12" y1="2" x2="12" y2="15"/>
                    </svg>
                    <span>Share</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <div className="floating-elements">
              <div className="float-card card-new animate-float delay-1">
                <span className="card-emoji">🔥</span>
                <div className="card-content">
                  <span className="card-title">New Post!</span>
                  <span className="card-time">Just now</span>
                </div>
              </div>
              <div className="float-card card-likes animate-float delay-2">
                <span className="card-emoji">❤️</span>
                <div className="card-content">
                  <span className="card-title">+127 likes</span>
                  <span className="card-time">Today</span>
                </div>
              </div>
              <div className="float-card card-msg animate-float delay-3">
                <span className="card-emoji">💬</span>
                <div className="card-content">
                  <span className="card-title">New message</span>
                  <span className="card-time">2m ago</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="stats-section" id="stats" ref={setRef('stats')}>
          <div className={`stats-container ${isVisible['stats'] ? 'visible' : ''}`}>
            {stats.map((stat, i) => (
              <div key={i} className="stat-item" style={{ animationDelay: `${i * 150}ms` }}>
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Just Dropped Section — featured products from the live shop.
            Visible to cold (unauthed) visitors so Reddit traffic sees
            real merchandise before the subscription wall. */}
        {onShopClick && justDropped.length > 0 && (
          <section className="just-dropped-section" id="just-dropped" ref={setRef('just-dropped')}>
            <div className="section-header">
              <span className="section-tag">✨ Just Dropped</span>
              <h2 className={`section-title ${isVisible['just-dropped'] ? 'animate-slide-up' : ''}`}>
                Shop the <span className="gradient-text">Closet</span>
              </h2>
              <p className="section-desc">Real 1-of-1 pieces from Jalen's life. Every purchase comes with exclusive photos.</p>
            </div>
            <div className={`just-dropped-grid ${isVisible['just-dropped'] ? 'visible' : ''}`}>
              {justDropped.map((p) => {
                const hero = p.images?.[0];
                const price = (p.salePrice || p.price).toFixed(0);
                return (
                  <button
                    type="button"
                    key={p.id}
                    className="drop-card"
                    onClick={onShopClick}
                    aria-label={`Shop ${p.name}`}
                  >
                    <div className="drop-card-image">
                      {hero ? (
                        <img src={hero} alt={p.name} loading="lazy" />
                      ) : (
                        <div className="drop-card-placeholder" aria-hidden="true">🧥</div>
                      )}
                      <span className="drop-card-badge">1 of 1</span>
                    </div>
                    <div className="drop-card-body">
                      <h3 className="drop-card-name">{p.name}</h3>
                      <span className="drop-card-unlock">✨ + Exclusive photos with purchase</span>
                      <div className="drop-card-price">
                        <span className="drop-card-price-label">Starting bid</span>
                        <span className="drop-card-price-amount">${price}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="just-dropped-cta">
              <button type="button" className="primary-btn shop-all-btn" onClick={onShopClick}>
                <span className="btn-content">
                  <span>See all live pieces</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </span>
              </button>
            </div>
          </section>
        )}

        {/* Pricing Section */}
        <section className="pricing-section" id="pricing" ref={setRef('pricing')}>
          <div className="section-header">
            <span className="section-tag">Flexible Plans</span>
            <h2 className={`section-title ${isVisible['pricing'] ? 'animate-slide-up' : ''}`}>
              Choose your <span className="gradient-text">access</span>
            </h2>
            <p className="section-desc">Subscribe now for unlimited access to exclusive content</p>
          </div>
          <div className={`pricing-grid ${isVisible['pricing'] ? 'visible' : ''}`}>
            {pricingPlans.map((plan, i) => (
              <div
                key={plan.id}
                className={`pricing-card ${plan.popular ? 'popular' : ''} ${plan.best ? 'best' : ''}`}
                style={{ animationDelay: `${i * 100}ms` }}
              >
                {plan.popular && <span className="plan-badge">MOST POPULAR</span>}
                {plan.best && <span className="plan-badge best-badge">BEST VALUE</span>}
                <h3>{plan.name}</h3>
                <div className="plan-price">
                  <span className="price-amount">${plan.price}</span>
                  <span className="price-period">{plan.period}</span>
                </div>
                {plan.savings && <span className="plan-savings">{plan.savings}</span>}
                <ul className="plan-features">
                  {plan.features.map((feature, j) => (
                    <li key={j}>
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button className="plan-btn" onClick={onGetStarted}>
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Social Media Section */}
        <section className="social-section" id="social" ref={setRef('social')}>
          <div className="section-header">
            <span className="section-tag">Follow Everywhere</span>
            <h2 className={`section-title ${isVisible['social'] ? 'animate-slide-up' : ''}`}>
              Join the <span className="gradient-text">community</span>
            </h2>
          </div>
          <div className={`social-grid ${isVisible['social'] ? 'visible' : ''}`}>
            {socialLinks.map((social, i) => (
              <a href={social.link} key={i} className="social-card" style={{ animationDelay: `${i * 100}ms` }}>
                <span className="social-icon">{social.icon}</span>
                <span className="social-platform">{social.platform}</span>
                <span className="social-followers">{social.followers}</span>
              </a>
            ))}
          </div>
        </section>

        {/* Content Types Section */}
        <section className="content-types-section" id="content" ref={setRef('content')}>
          <div className="section-header">
            <span className="section-tag">What You Get</span>
            <h2 className={`section-title ${isVisible['content'] ? 'animate-slide-up' : ''}`}>
              Exclusive <span className="gradient-text">content</span> library
            </h2>
            <p className="section-desc">Access hundreds of exclusive posts and growing daily</p>
          </div>
          <div className={`content-grid ${isVisible['content'] ? 'visible' : ''}`}>
            {contentTypes.map((content, i) => (
              <div key={i} className="content-type-card" style={{ animationDelay: `${i * 100}ms` }}>
                <span className="content-icon">{content.icon}</span>
                <h3>{content.title}</h3>
                <span className="content-count">{content.count}</span>
                <p>{content.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Preview Gallery Section */}
        <section className="gallery-section" id="gallery" ref={setRef('gallery')}>
          <div className="section-header">
            <span className="section-tag">Sneak Peek</span>
            <h2 className={`section-title ${isVisible['gallery'] ? 'animate-slide-up' : ''}`}>
              Preview the <span className="gradient-text">exclusives</span>
            </h2>
          </div>
          <div className={`gallery-grid ${isVisible['gallery'] ? 'visible' : ''}`}>
            {galleryImages.map((img, i) => (
              <div key={i} className="gallery-item" style={{ animationDelay: `${i * 100}ms` }}>
                <img src={img} alt={`Preview ${i + 1}`} />
                <div className="gallery-overlay">
                  <div className="gallery-lock">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                    </svg>
                  </div>
                  <span>Subscribe to unlock</span>
                </div>
              </div>
            ))}
          </div>
          <div className="gallery-cta">
            <button className="secondary-btn" onClick={onGetStarted}>
              <span>See All Content</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="benefits-section" id="benefits" ref={setRef('benefits')}>
          <div className="section-header">
            <span className="section-tag">Why Subscribe</span>
            <h2 className={`section-title ${isVisible['benefits'] ? 'animate-slide-up' : ''}`}>
              Exclusive perks for <span className="gradient-text">members</span>
            </h2>
          </div>

          <div className="benefits-grid">
            {benefits.map((benefit, i) => (
              <div
                key={i}
                className={`benefit-card ${isVisible['benefits'] ? 'visible' : ''}`}
                style={{ animationDelay: `${benefit.delay}ms` }}
              >
                <div className="benefit-icon-wrapper">
                  <span className="benefit-icon">{benefit.icon}</span>
                  <div className="icon-bg" />
                </div>
                <h3>{benefit.title}</h3>
                <p>{benefit.desc}</p>
                <div className="card-shine" />
              </div>
            ))}
          </div>
        </section>

        {/* How It Works Section */}
        <section className="how-it-works-section" id="how" ref={setRef('how')}>
          <div className="section-header">
            <span className="section-tag">Easy Steps</span>
            <h2 className={`section-title ${isVisible['how'] ? 'animate-slide-up' : ''}`}>
              How it <span className="gradient-text">works</span>
            </h2>
          </div>
          <div className={`steps-container ${isVisible['how'] ? 'visible' : ''}`}>
            {howItWorks.map((step, i) => (
              <div key={i} className="step-card" style={{ animationDelay: `${i * 150}ms` }}>
                <div className="step-number">{step.step}</div>
                <span className="step-icon">{step.icon}</span>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
                {i < howItWorks.length - 1 && <div className="step-connector" />}
              </div>
            ))}
          </div>
        </section>

        {/* Social Proof */}
        <section className="social-proof-section" id="proof" ref={setRef('proof')}>
          <div className={`proof-content ${isVisible['proof'] ? 'visible' : ''}`}>
            <div className="subscriber-avatars">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="avatar-circle" style={{ animationDelay: `${i * 100}ms` }}>
                  <div className="avatar-placeholder" style={{ background: `hsl(${i * 60}, 70%, 60%)` }} />
                </div>
              ))}
              <div className="avatar-more">+25K</div>
            </div>
            <p className="proof-text">
              <span className="highlight">25,000+</span> subscribers already enjoying exclusive content
            </p>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section" id="faq" ref={setRef('faq')}>
          <div className="section-header">
            <span className="section-tag">Questions?</span>
            <h2 className={`section-title ${isVisible['faq'] ? 'animate-slide-up' : ''}`}>
              Frequently <span className="gradient-text">asked</span>
            </h2>
          </div>
          <div className={`faq-container ${isVisible['faq'] ? 'visible' : ''}`}>
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`faq-item ${activeFaq === i ? 'active' : ''}`}
                onClick={() => setActiveFaq(activeFaq === i ? null : i)}
              >
                <div className="faq-question">
                  <h3>{faq.q}</h3>
                  <span className="faq-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 9l-7 7-7-7"/>
                    </svg>
                  </span>
                </div>
                <div className="faq-answer">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Trust Badges */}
        <section className="trust-section" id="trust" ref={setRef('trust')}>
          <div className={`trust-badges ${isVisible['trust'] ? 'visible' : ''}`}>
            <div className="trust-badge">
              <span className="trust-icon">🔒</span>
              <span className="trust-text">Secure Payments</span>
            </div>
            <div className="trust-badge">
              <span className="trust-icon">✓</span>
              <span className="trust-text">Cancel Anytime</span>
            </div>
            <div className="trust-badge">
              <span className="trust-icon">⚡</span>
              <span className="trust-text">Instant Access</span>
            </div>
            <div className="trust-badge">
              <span className="trust-icon">💬</span>
              <span className="trust-text">24/7 Support</span>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="cta-section" id="cta" ref={setRef('cta')}>
          <div className={`cta-content ${isVisible['cta'] ? 'visible' : ''}`}>
            <div className="cta-bg-elements">
              <div className="cta-circle circle-1" />
              <div className="cta-circle circle-2" />
            </div>
            <h2>Ready to join the inner circle?</h2>
            <p>Get instant access to all exclusive content today</p>
            <div className="cta-price-preview">
              <span className="cta-price">Starting at just $25/month</span>
            </div>
            <button className="cta-btn" onClick={onGetStarted}>
              <span className="btn-text">Subscribe Now</span>
              <span className="btn-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </span>
              <div className="btn-ripple" />
            </button>
            <span className="cta-note">Cancel anytime • Instant access • Secure payment</span>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <img src="https://jalenward.com/images/logosmall.png" alt="OnlyJalen" className="footer-logo" style={{ filter: 'brightness(0) invert(1)' }} />
            <p>Exclusive content and direct connection with Jalen Ward.</p>
            <div className="footer-social">
              <a href="https://instagram.com/built_by_ward" className="footer-social-link" target="_blank" rel="noopener noreferrer">📸</a>
              <a href="https://www.tiktok.com/@builtbyward" className="footer-social-link" target="_blank" rel="noopener noreferrer">🎵</a>
              <span className="footer-social-link" style={{ opacity: 0.4, cursor: 'default' }}>🐦</span>
              <span className="footer-social-link" style={{ opacity: 0.4, cursor: 'default' }}>▶️</span>
            </div>
          </div>
          <div className="footer-links-container">
            <div className="footer-links-group">
              <h4>Quick Links</h4>
              <a href="#content">Content</a>
              <a href="#pricing">Pricing</a>
              <a href="#faq">FAQ</a>
            </div>
            <div className="footer-links-group">
              <h4>Legal</h4>
              <a href="/privacy">Privacy Policy</a>
              <a href="/terms">Terms of Service</a>
              <a href="/privacy#cookies">Cookie Policy</a>
            </div>
            <div className="footer-links-group">
              <h4>Support</h4>
              <a href="mailto:support@onlyjalen.com">Contact Us</a>
              <a href="#faq">Help Center</a>
              <a href="mailto:support@onlyjalen.com?subject=Issue%20Report">Report Issue</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2026 OnlyJalen. All rights reserved.</p>
          <div className="payment-methods">
            <span>💳</span>
            <span>🏦</span>
            <span>📱</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
